import { t } from '../lib/i18n';

/**
 * What to tell an agent when a message did not go out (Phase 87).
 *
 * The thread was showing the provider's own words, in English, unedited: a real conversation ended
 * with a red line reading `session service answered 500: {"error":"t"}` under an audio bubble. That
 * sentence is true and useless — `t` is a minified name from inside WhatsApp Web's bundle — and it
 * is the only thing the person who recorded that audio was given.
 *
 * So the reason is translated into something a person can act on, and the original is kept as the
 * `title` for whoever has to ask about it. Nothing is hidden; it is put where each reader needs it.
 *
 * Matched on substrings rather than on codes because these strings come from three different
 * adapters and are not a contract. A reason nobody has mapped falls through to the honest general
 * answer, which is still better than the provider's grammar.
 */

interface Rule {
  when: RegExp;
  key: string;
  /** Whether trying the same message again has any chance of working. */
  retryable: boolean;
}

const RULES: Rule[] = [
  // The unofficial session refused it, or threw something unreadable from inside WhatsApp's bundle.
  { when: /session service answered|\{"error"/i, key: 'refused', retryable: true },
  {
    when: /is not accepting messages|not answered a health check/i,
    key: 'offline',
    retryable: true,
  },
  { when: /has no paired whatsapp session/i, key: 'unpaired', retryable: false },
  { when: /blocked this number|banned/i, key: 'blocked', retryable: false },
  { when: /templates belong to|approved template/i, key: 'template', retryable: false },
  { when: /not a phone number|not an address/i, key: 'address', retryable: false },
  { when: /attachment could not be read/i, key: 'attachment', retryable: true },
  { when: /conversation or its channel is gone/i, key: 'channel', retryable: false },
  { when: /window|24-hour/i, key: 'window', retryable: false },
];

export interface ReadableFailure {
  /** What the agent reads. */
  text: string;
  /** The provider's own words, for a tooltip and for support. */
  detail: string;
  /** Whether to offer sending it again. */
  retryable: boolean;
}

export function readableFailure(reason: string): ReadableFailure {
  const rule = RULES.find((candidate) => candidate.when.test(reason));

  return {
    text: t(`inbox.failure.${rule?.key ?? 'unknown'}`),
    detail: reason,
    // An unmapped failure is offered a retry: most transport failures are transient, and a button
    // that does nothing is a smaller harm than a message nobody can send again.
    retryable: rule?.retryable ?? true,
  };
}
